import {
  AppBar,
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Radio,
  Select,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import moment from 'moment-timezone';
import React, { ChangeEvent } from 'react';

import { getBidderGroups, IBidderGroup } from '~/src/dash/api/bidder-group';
import {
  BrandSafetyThirdPartyDoubleVerify,
  BrandSafetyThirdPartyIAS,
  BrandSafetyThirdPartyMoat,
  BrandSafetyThirdPartyOther,
  BrandSafetyThirdPartyPixalate,
  BrandSafetyThirdPartyProtectedMedia,
  BrandSafetyThirdPartyUnknown,
  BrandSafetyTrackerActiveActive,
  BrandSafetyTrackerActiveInactive,
  BrandSafetyTrackerAlwaysOnActive,
  BrandSafetyTrackerAlwaysOnInactive,
  BrandSafetyTrackerBidderTargetingAll,
  BrandSafetyTrackerBidderTargetingSelected,
  BrandSafetyTrackerChannelTargetingAll,
  BrandSafetyTrackerChannelTargetingSelected,
  BrandSafetyTrackerEventActiveActive,
  BrandSafetyTrackerEventActiveInactive,
  BrandSafetyTrackerEventTypeComplete,
  BrandSafetyTrackerEventTypeImpression,
  BrandSafetyTrackerEventTypeQuartile1,
  BrandSafetyTrackerEventTypeQuartile2,
  BrandSafetyTrackerEventTypeQuartile3,
  BrandSafetyTrackerEventTypeStart,
  BrandSafetyTrackerEventTypeUnknown,
  createBrandSafetyTracker,
  IBrandSafetyTracker,
  IBrandSafetyTrackerEvent,
  updateBrandSafetyTracker,
} from '~/src/dash/api/brand-safety-tracker';
import { getChannelGroupsPromise } from '~/src/dash/api/channel-group';
import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { IChannelGroup, IHeaderBidder, queryHeaderBidders } from '~/src/dash/api/console-api';
import { createUrlWithParam, IHttpResponse, restDelete } from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import ElevationScroll from '~/src/dash/components/model-elevation-scroll';
import { ModelPrimaryButton } from '~/src/dash/components/model-primary-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ModernTextField } from '~/src/dash/components/modern-text-field';
import { SearchDropdownSingle } from '~/src/dash/components/search-dropdown-single';
import { ITrackerEvent, TrackerEvents } from '~/src/dash/components/tracker-events';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { BrandSafetyReferrerBrandSafetyTracker } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  RouteBrandSafetyList,
  RouteBrandSafetyTrackerEdit,
} from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import {
  formatDateFromUTCToOffset,
  formatDateWithOffsetToUTC,
  validateDatetimes,
} from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { filterSitesByActive } from '~/src/dash/utils/models';
import {
  obfuscateBidderGroupName,
  obfuscateBidderName,
  obfuscateChannelGroupName,
  obfuscateSiteName,
} from '~/src/dash/utils/obfuscate';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { IDedupMap } from '~/src/dash/views/bidder-report-form-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  tracker: IBrandSafetyTracker;
  error: string;
}

interface IState extends IBaseState, IBrandSafetyTracker {
  loading: boolean;
  updating: boolean;
  snackbarMessage: string;
  snackbarError: boolean;
  // delete confirmation popup
  deletePopupActive: boolean;
  deletePopupEventID: number;
  deletePopupEventName: string;
  startDateMoment: moment.Moment;
  endDateMoment: moment.Moment;
  timezonesList: IMenuList;
  channelList: IMenuList;
  bidderList: IMenuList;
  channelGroupList: IMenuList;
  bidderGroupList: IMenuList;
  trackerURLsWithErrors: number[];
}

const BrandSafetyThirdParties = [
  { Name: 'IAS', Value: BrandSafetyThirdPartyIAS },
  { Name: 'Protected Media', Value: BrandSafetyThirdPartyProtectedMedia },
  { Name: 'Moat', Value: BrandSafetyThirdPartyMoat },
  { Name: 'DoubleVerify', Value: BrandSafetyThirdPartyDoubleVerify },
  { Name: 'Pixalate', Value: BrandSafetyThirdPartyPixalate },
  { Name: 'Other', Value: BrandSafetyThirdPartyOther },
];

const BrandSafetyEventType = [
  { Name: 'Impression', Value: BrandSafetyTrackerEventTypeImpression },
  { Name: 'Start', Value: BrandSafetyTrackerEventTypeStart },
  { Name: 'Quartile 1', Value: BrandSafetyTrackerEventTypeQuartile1 },
  { Name: 'Quartile 2', Value: BrandSafetyTrackerEventTypeQuartile2 },
  { Name: 'Quartile 3', Value: BrandSafetyTrackerEventTypeQuartile3 },
  { Name: 'Complete', Value: BrandSafetyTrackerEventTypeComplete },
];

export const BrandSafetyTrackerEdit = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingLeft: theme.spacing.unit * 2 + 'px !important',
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 2,
        width: '100%',
      },
      mainGridItem: {
        marginLeft: 40,
        marginTop: 5,
        width: '100%',
        marginRight: 40,
        maxWidth: 800,
      },
      titleTypography: {
        fontWeight: 500,
        fontSize: '0.875rem',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      // insert rule in state
      ...this.props.tracker,
      loading: false,
      updating: false,
      snackbarMessage: this.props.error,
      snackbarError: false,
      startDateMoment: formatDateFromUTCToOffset(
        this.props.tracker.StartDate,
        this.props.tracker.Timezone || this.props.session.publisher.Timezone,
      ),
      endDateMoment: formatDateFromUTCToOffset(
        this.props.tracker.EndDate,
        this.props.tracker.Timezone || this.props.session.publisher.Timezone,
      ),
      // delete confirmation popup
      deletePopupActive: false,
      timezonesList: {} as IMenuList,
      deletePopupEventID: 0,
      deletePopupEventName: '',
      channelList: {},
      bidderList: {},
      channelGroupList: {},
      bidderGroupList: {},
      trackerURLsWithErrors: [] as number[],
    };

    // initialization function
    componentDidMount() {
      this.genTimezoneList();
      this.genChannelListAndGetChannelGroups();
      this.genBidderListAndGetBidderGroups();
    }

    // generates timezone list for dropdown
    genTimezoneList = () => {
      const timezonesList = genTimezoneList(
        this.state.Timezone || this.props.session.publisher.Timezone,
      );
      this.setState({
        timezonesList,
      });
    };

    // generates channel list for dropdown and gets channel groups
    // calls refreshManyModels after channel groups selected
    // if the no selected sites in props, by default, all sites are selected
    // selected sites are saved into props for navigation back to this view
    genChannelListAndGetChannelGroups = async () => {
      const channelList: IMenuList = {};

      const [small_sites, err] = await getSmallSites(this.props.session.publisher.ID);
      if (err) {
        console.error(err);
        return;
      }

      const activeSites = filterSitesByActive(small_sites || []);
      const selectedSiteIDsMap = {} as IDedupMap;

      for (const siteID of this.state.SiteIDS) {
        selectedSiteIDsMap[siteID] = true;
      }

      activeSites.forEach((site: ISmallSite) => {
        const title = obfuscateSiteName(site, this.props.user.user.Username, site.Name);
        channelList[site.ID] = {
          title,
          selected: site.ID in selectedSiteIDsMap,
          ID: site.ID.toString(),
          small_site: site,
        };
      });

      getChannelGroupsPromise({
        publisherID: this.props.session.publisher.ID,
      }).then(({ channelGroups }: { channelGroups: IChannelGroup[] }) => {
        const channelGroupList: IMenuList = {};
        const selectedCGsMap = {} as IDedupMap;
        for (const cgID of this.state.ChannelGroupIDS) {
          selectedCGsMap[cgID] = true;
        }

        channelGroups.forEach((cg: IChannelGroup) => {
          const title = obfuscateChannelGroupName(
            cg,
            this.props.user.user.Username,
            `[${cg.ID}] ${cg.Name}`,
          );
          channelGroupList[cg.ID] = {
            title,
            selected: cg.ID in selectedCGsMap,
            ID: cg.ID.toString(),
            channelGroup: cg,
          };
        });
        this.setState({
          channelGroupList,
          channelList,
        });
      });
    };

    genBidderListAndGetBidderGroups = () => {
      queryHeaderBidders({
        query: {
          publisher_id: this.props.session.publisher.ID,
          site_ids: '',
          include_deleted: false,
        },
      }).then((bidders: IHeaderBidder[]) => {
        const bidderList: IMenuList = {};
        const selectedBidderIDsMap = {} as IDedupMap;
        for (const bidderID of this.state.BidderIDS) {
          selectedBidderIDsMap[bidderID] = true;
        }

        for (const hb of bidders) {
          const title = obfuscateBidderName(hb, this.props.user.user.Username, hb.Name);
          bidderList[hb.ID] = {
            title,
            selected: hb.ID in selectedBidderIDsMap,
            ID: hb.ID.toString(),
            hb,
          };
        }

        this.setState({
          bidderList,
        });
      });

      getBidderGroups({
        query: {
          publisher_id: this.props.session.publisher.ID,
        },
      }).then(({ bidderGroups }: { bidderGroups: IBidderGroup[] }) => {
        const bidderGroupList: IMenuList = {};
        const selectedBGsMap = {} as IDedupMap;
        for (const bgID of this.state.BidderGroupIDS) {
          selectedBGsMap[bgID] = true;
        }

        bidderGroups.forEach((bg: IBidderGroup) => {
          const title = obfuscateBidderGroupName(
            bg,
            this.props.user.user.Username,
            `[${bg.ID}] ${bg.Name}`,
          );
          bidderGroupList[bg.ID] = {
            title,
            selected: bg.ID in selectedBGsMap,
            ID: bg.ID.toString(),
            bidderGroup: bg,
          };
        });
        this.setState({
          bidderGroupList,
        });
      });
    };

    // handle change in the tracker's active switch
    handleTrackerActiveSwitchChange = (checked: boolean) => {
      this.setState({
        Active: checked ? BrandSafetyTrackerActiveActive : BrandSafetyTrackerActiveInactive,
      });
    };

    // -------- TRACKER EVENTS HELPER FUNCTIONS -------- //
    // handle change in the tracker event's active switch
    handleTrackerEventActiveSwitchChange = (index: number) => {
      const trackerEvents = this.state.BrandSafetyTrackerEvents;
      if (index < trackerEvents.length) {
        trackerEvents[index].Active =
          trackerEvents[index].Active === BrandSafetyTrackerEventActiveInactive
            ? BrandSafetyTrackerEventActiveActive
            : BrandSafetyTrackerEventActiveInactive;
      }
      this.setState({
        BrandSafetyTrackerEvents: trackerEvents,
      });
    };

    // handle change in the tracker event's pixel URL
    handleTrackerEventPixelURLChange = (text: string, index: number) => {
      const trackerEvents = this.state.BrandSafetyTrackerEvents;
      if (index < trackerEvents.length) {
        trackerEvents[index].PixelURL = text;
      }
      this.setState({
        BrandSafetyTrackerEvents: trackerEvents,
      });
    };

    handleTrackerFireOncePerPodChange = (index: number) => {
      const trackerEvents = this.state.BrandSafetyTrackerEvents;
      if (index < trackerEvents.length) {
        trackerEvents[index].FireOncePerPod =
          trackerEvents[index].FireOncePerPod === EBool.EBool_TRUE
            ? EBool.EBool_FALSE
            : EBool.EBool_TRUE;
      }
      this.setState({
        BrandSafetyTrackerEvents: trackerEvents,
      });
    };

    // handle change in the tracker event's event type
    handleTrackerEventTypeChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number,
    ) => {
      const events = this.state.BrandSafetyTrackerEvents;
      events[index].EventType =
        parseInt(e.target.value, 10) !== undefined ? parseInt(e.target.value, 10) : 0;
      this.setState({
        BrandSafetyTrackerEvents: events,
      });
    };

    // returns promise for deleting event
    deleteEventWithPromise = (id: number) => {
      const reqURL = createUrlWithParam(
        `/brand_safety_tracker_event`,
        this.props.session.publisher.ID,
        id.toString(),
        'event_id',
      );
      return restDelete<IBrandSafetyTrackerEvent>(reqURL, null);
    };

    // executed when the delete popup fails
    handleDeleteError = () => {
      this.setState({
        snackbarError: true,
        snackbarMessage: 'Tracker event deletion failed',
        deletePopupActive: false,
      });
    };

    // executed when the delete popup is closed
    handleDeleteSuccess = (index: number) => {
      const events = [...this.state.BrandSafetyTrackerEvents];
      if (index !== undefined) {
        events.splice(index, 1);
        this.setState({
          BrandSafetyTrackerEvents: events,
          snackbarError: false,
          snackbarMessage: 'Event successfully deleted',
          deletePopupActive: false,
        });
        return;
      }
    };

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        [target.name]: target.value,
      });
    };

    // close the notification snackbar
    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // handles the user click on the close button
    handleCloseButtonClick = () => {
      this.props.session.brandSafetyReferrer = BrandSafetyReferrerBrandSafetyTracker; // set referrer for the 'close' button
      navigate(
        RouteBrandSafetyList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // timezone dropdown
    handleTimezoneAcceptClick = (): void => {
      for (const { title, selected } of Object.values(this.state.timezonesList)) {
        if (selected) {
          this.setState({
            Timezone: title,
          });
          break;
        }
      }
    };

    // DateTimeRangeComponent Date Change Handler
    handleDateChange = ({ start, end }: IDateRangeChangeEvent) => {
      this.setState({
        startDateMoment: start,
        endDateMoment: end,
      });
    };

    validateTrackerInfo = (): [boolean, string] => {
      let valid = true;
      const msgs = ['Save failed.'];
      if (this.state.ThirdParty === BrandSafetyThirdPartyUnknown) {
        valid = false;
        msgs.push('Tracker third party cannot be unknown.');
      }

      let errorMsgs = validateDatetimes(
        this.state.startDateMoment,
        this.state.endDateMoment,
        undefined,
      );
      if (errorMsgs.length > 0) {
        valid = false;
        msgs.push(...errorMsgs);
      }

      for (const event of this.state.BrandSafetyTrackerEvents) {
        errorMsgs = this.validateEvent(event);
        if (errorMsgs.length > 0) {
          valid = false;
          msgs.push(...errorMsgs);
        }
      }
      if (this.state.trackerURLsWithErrors.length > 0) {
        valid = false;
        msgs.push('At least one tracker URL is invalid');
      }
      const msg = msgs.join(' ');
      return [valid, msg];
    };

    // saves tracker and updates/creates child events, validates events and tracker fields
    saveTracker = () => {
      // validate current info
      const [valid, msg] = this.validateTrackerInfo();
      if (!valid) {
        this.setState({
          snackbarMessage: msg,
          snackbarError: true,
        });
        return;
      }
      const tracker: IBrandSafetyTracker = {
        ID: this.state.ID,
        PublisherID: this.state.PublisherID,
        Name: this.state.Name,
        Active: this.state.Active,
        ThirdParty: this.state.ThirdParty,
        AlwaysOn: this.state.AlwaysOn,
        StartDate: formatDateWithOffsetToUTC(this.state.startDateMoment, this.state.Timezone),
        EndDate: formatDateWithOffsetToUTC(this.state.endDateMoment, this.state.Timezone),
        Timezone: this.state.Timezone,
        MeasuredInventory: this.state.MeasuredInventory,
        BrandSafetyTrackerEvents: this.state.BrandSafetyTrackerEvents,
        SiteIDS: this.state.SiteIDS,
        ChannelGroupIDS: this.state.ChannelGroupIDS,
        BidderIDS: this.state.BidderIDS,
        BidderGroupIDS: this.state.BidderGroupIDS,
        ChannelTargeting: this.state.ChannelTargeting,
      };
      // if the tracker is not targeting channels, empty SiteIDS and ChannelGroupIDS
      if (tracker.ChannelTargeting !== BrandSafetyTrackerChannelTargetingSelected) {
        tracker.SiteIDS = [];
        tracker.ChannelGroupIDS = [];
      }
      // if the tracker is not targeting bidders, empty SiteIDS and ChannelGroupIDS
      if (tracker.ChannelTargeting !== BrandSafetyTrackerBidderTargetingSelected) {
        tracker.BidderIDS = [];
        tracker.BidderGroupIDS = [];
      }
      // if tracker is new, create a new tracker in DB and navigate to page
      if (tracker.ID === 0) {
        createBrandSafetyTracker(tracker)
          .then((val: IHttpResponse<IBrandSafetyTracker>) => {
            const tracker = val.parsedBody as IBrandSafetyTracker;
            navigate(RouteBrandSafetyTrackerEdit, extractRouteParams(this.props), {
              brandSafetyTrackerID: tracker.ID.toString(),
            });
            // });
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'Tracker creation failed',
              snackbarError: true,
            });
          });
      } else {
        updateBrandSafetyTracker(tracker)
          .then((val: IHttpResponse<IBrandSafetyTracker>) => {
            this.setState({
              ...(val.parsedBody as IBrandSafetyTracker),
              snackbarMessage: 'Tracker updated successfully',
              snackbarError: false,
            });
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'Tracker update failed',
              snackbarError: true,
            });
          });
      }
    };

    setStateCallback = (keys: string[], values: any[], setStateCallbacks: Array<() => void>) => {
      const stateObj = {} as IState;
      for (let i = 0; i < keys.length; i++) {
        stateObj[keys[i]] = values[i];
      }
      this.setState(stateObj, () => {
        setStateCallbacks.forEach((callback: () => void) => callback());
      });
    };

    // returns promise for deleting event
    handleDeleteModelWithPromise = (eventID: number) => {
      const reqURL = createUrlWithParam(
        `/brand_safety_tracker_event`,
        this.props.session.publisher.ID,
        eventID.toString(),
        'event_id',
      );
      return restDelete<IBrandSafetyTrackerEvent>(reqURL, null);
    };

    // deletes a new event (unsaved) without the pop up / promise
    deleteNewEvent = (idx: number) => {
      const events = [...this.state.BrandSafetyTrackerEvents];
      events.splice(idx, 1);
      this.setState({
        BrandSafetyTrackerEvents: events,
      });
    };

    // handles the deletion of an event
    showDeleteEventPopup = (idx: number) => {
      const event = this.state.BrandSafetyTrackerEvents[idx];
      this.setState({
        deletePopupActive: true,
        deletePopupEventID: event.ID,
        deletePopupEventName: `Tracker ID: ${event.ID}`,
      });
    };

    // adds new blank event
    addEvent = () => {
      const newEvent = {
        ID: 0,
        BrandSafetyTrackerID: this.state.ID,
        Active: BrandSafetyTrackerEventActiveActive,
        EventType: BrandSafetyTrackerEventTypeUnknown,
        PixelURL: '',
        FireOncePerPod: EBool.EBool_FALSE,
      };
      const events = [...this.state.BrandSafetyTrackerEvents];
      events.push(newEvent);
      this.setState({
        BrandSafetyTrackerEvents: events,
      });
    };

    validateEvent = (event: IBrandSafetyTrackerEvent) => {
      const errorMsgs = [];
      if (event.EventType === BrandSafetyTrackerEventTypeUnknown) {
        errorMsgs.push(
          `Event [${event.ID === 0 ? 'New' : event.ID.toString()}] type cannot be unknown.`,
        );
      }
      if (event.PixelURL === '') {
        errorMsgs.push(
          `Event [${event.ID === 0 ? 'New' : event.ID.toString()}] URL cannot be empty.`,
        );
      }
      return errorMsgs;
    };

    updateTrackerURLsWithErrors = (action: string, trackerURLID: number) => {
      const updatedErrorsArray = [...this.state.trackerURLsWithErrors].filter(
        (urlID) => urlID !== trackerURLID,
      );
      if (action === 'add') {
        updatedErrorsArray.push(trackerURLID);
      }
      this.setState({
        trackerURLsWithErrors: updatedErrorsArray,
      });
    };

    handleChannelAcceptClick = (list: IMenuList): void => {
      const SiteIDS = [] as number[];
      for (const { selected, ID } of Object.values(list)) {
        if (selected) {
          SiteIDS.push(+ID);
        }
      }

      this.setState({
        channelList: list,
        SiteIDS,
      });
    };

    handleBidderAcceptClick = (list: IMenuList): void => {
      const BidderIDS = [] as number[];
      for (const { selected, ID } of Object.values(list)) {
        if (selected) {
          BidderIDS.push(+ID);
        }
      }

      this.setState({
        bidderList: list,
        BidderIDS,
      });
    };

    handleChannelGroupAcceptClick = (list: IMenuList): void => {
      const ChannelGroupIDS = [] as number[];
      for (const { selected, ID } of Object.values(list)) {
        if (selected) {
          ChannelGroupIDS.push(+ID);
        }
      }

      this.setState({
        channelGroupList: list,
        ChannelGroupIDS,
      });
    };

    handleBidderGroupAcceptClick = (list: IMenuList): void => {
      const BidderGroupIDS = [] as number[];
      for (const { selected, ID } of Object.values(list)) {
        if (selected) {
          BidderGroupIDS.push(+ID);
        }
      }

      this.setState({
        bidderGroupList: list,
        BidderGroupIDS,
      });
    };

    // ############################## RENDER TOP BAR WITH RULE NAME AND ACTIVE SWITCH ##############################
    renderTopBar(classes: { [key: string]: string }) {
      // rendering
      return (
        <Grid item>
          <ElevationScroll {...this.props}>
            <AppBar
              position='relative'
              elevation={0}
              style={{
                backgroundColor: '#F8faff',
                border: '#cbcbcb',
                borderTopStyle: 'solid',
                borderBottomStyle: 'solid',
                borderTopWidth: 'thin',
                borderBottomWidth: 'thin',
                position: 'fixed',
              }}
            >
              <Toolbar>
                <Grid
                  container
                  direction='row'
                  justify='space-between'
                  alignItems='center'
                  style={{ maxWidth: 800 }}
                >
                  {/* TRACKER NAME*/}
                  <Grid item>
                    <ModernTextField
                      value={
                        checkUserInfoMasked(this.props.user.user)
                          ? 'demo_tracker_' + this.state.ID
                          : this.state.Name
                      }
                      onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
                        this.setState({
                          Name: target.value,
                        });
                      }}
                    />
                  </Grid>

                  {/* ACTIVE SWITCH*/}
                  <Grid item style={{ paddingLeft: '20px' }}>
                    <span
                      style={{
                        color: 'black',
                        fontSize: 14,
                      }}
                    >
                      Active
                    </span>
                    <Switch
                      checked={this.state.Active === BrandSafetyTrackerActiveActive}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        this.handleTrackerActiveSwitchChange(event.target.checked);
                      }}
                    />
                  </Grid>

                  <Grid item>
                    {/* SAVE BUTTON*/}
                    <ModelPrimaryButton variant='contained' onClick={() => this.saveTracker()}>
                      Save
                    </ModelPrimaryButton>
                    {/* CLOSE BUTTON*/}
                    <Button
                      style={{ color: '#707070' }}
                      className={classes.button}
                      onClick={this.handleCloseButtonClick}
                    >
                      Close
                    </Button>
                  </Grid>
                </Grid>
              </Toolbar>
            </AppBar>
          </ElevationScroll>
          <Toolbar />
        </Grid>
      );
    }

    // ############################## RENDER TRACKER DETAILS ##############################
    renderTrackerDetails = () => {
      const alwaysOn = this.state.AlwaysOn === BrandSafetyTrackerAlwaysOnActive;
      const { classes } = this.props;
      return (
        <Grid item className={classes.mainGridItem}>
          <Typography style={{ marginBottom: 10, marginTop: 20, fontWeight: 500, fontSize: 18 }}>
            Tracker Details
          </Typography>
          <Paper className={classes.paper}>
            <Grid
              container
              direction='column'
              justify='flex-start'
              alignItems='flex-start'
              spacing={1}
            >
              {/* Third Party */}
              <Grid item>
                <Grid
                  container
                  direction='row'
                  justify='flex-start'
                  alignItems='center'
                  spacing={3}
                >
                  <Grid item style={{ width: 150 }}>
                    <Typography className={classes.titleTypography}>Third Party</Typography>
                  </Grid>
                  <Grid item>
                    <Select
                      style={{
                        width: '160px',
                      }}
                      value={this.state.ThirdParty}
                      onChange={(ev: ChangeEvent<{ name: string; value: number }>) => {
                        this.setState({
                          ThirdParty: ev.target.value,
                        });
                      }}
                    >
                      {BrandSafetyThirdParties.map((party) => {
                        return <MenuItem value={party.Value}>{party.Name}</MenuItem>;
                      })}
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
              {/* Channel / Group Targeting */}
              <Grid item>
                <Grid
                  container
                  direction='column'
                  justify='flex-start'
                  alignItems='flex-start'
                  spacing={1}
                >
                  <Grid item>
                    <Typography className={classes.titleTypography}>Targeting</Typography>
                  </Grid>
                  {/* Activation - Radio Buttons for Always on vs Date Range */}
                  <Grid item>
                    <Grid
                      container
                      direction='row'
                      justify='flex-start'
                      alignItems='flex-start'
                      spacing={0}
                    >
                      <Grid
                        item
                        container
                        direction='row'
                        justify='flex-start'
                        alignItems='center'
                        spacing={1}
                      >
                        <Radio
                          onChange={() =>
                            this.setState({
                              ChannelTargeting: BrandSafetyTrackerChannelTargetingAll,
                            })
                          }
                          checked={
                            this.state.ChannelTargeting === BrandSafetyTrackerChannelTargetingAll
                          }
                        />
                        <Typography style={{ fontSize: 12, color: alwaysOn ? 'black' : 'grey' }}>
                          All Channels
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        container
                        direction='row'
                        justify='flex-start'
                        alignItems='center'
                        spacing={1}
                      >
                        <Radio
                          onChange={() =>
                            this.setState({
                              ChannelTargeting: BrandSafetyTrackerChannelTargetingSelected,
                            })
                          }
                          checked={
                            this.state.ChannelTargeting ===
                            BrandSafetyTrackerChannelTargetingSelected
                          }
                        />
                        <Typography style={{ fontSize: 12, color: alwaysOn ? 'grey' : 'black' }}>
                          Selected Channels and Groups
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        container
                        direction='row'
                        justify='flex-start'
                        alignItems='center'
                        spacing={1}
                      >
                        <Radio
                          onChange={() =>
                            this.setState({
                              ChannelTargeting: BrandSafetyTrackerBidderTargetingAll,
                            })
                          }
                          checked={
                            this.state.ChannelTargeting === BrandSafetyTrackerBidderTargetingAll
                          }
                        />
                        <Typography style={{ fontSize: 12, color: alwaysOn ? 'grey' : 'black' }}>
                          All Bidders
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        container
                        direction='row'
                        justify='flex-start'
                        alignItems='center'
                        spacing={1}
                      >
                        <Radio
                          onChange={() =>
                            this.setState({
                              ChannelTargeting: BrandSafetyTrackerBidderTargetingSelected,
                            })
                          }
                          checked={
                            this.state.ChannelTargeting ===
                            BrandSafetyTrackerBidderTargetingSelected
                          }
                        />
                        <Typography style={{ fontSize: 12, color: alwaysOn ? 'grey' : 'black' }}>
                          Selected Bidders and Groups
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  {this.state.ChannelTargeting === BrandSafetyTrackerChannelTargetingSelected && (
                    <Grid item style={{ width: '400px', marginLeft: 38, marginBottom: 10 }}>
                      <MaterialSearchDropdownMulti
                        getValueFromSearchElement={(src: IListItem) => src.title}
                        selectAll={true}
                        menuTitle='Channels'
                        list={this.state.channelList}
                        onChange={(list: IMenuList) => {
                          this.setState({
                            channelList: list,
                          });
                        }}
                        handleMenuAcceptClick={this.handleChannelAcceptClick}
                        showPrefixID
                      />
                    </Grid>
                  )}
                  {this.state.ChannelTargeting === BrandSafetyTrackerChannelTargetingSelected && (
                    <Grid item style={{ width: '400px', marginLeft: 38, marginBottom: 10 }}>
                      <MaterialSearchDropdownMulti
                        getValueFromSearchElement={(src: IListItem) => src.title}
                        selectAll={true}
                        menuTitle='Channel Groups'
                        list={this.state.channelGroupList}
                        onChange={(list: IMenuList) => {
                          this.setState({
                            channelGroupList: list,
                          });
                        }}
                        handleMenuAcceptClick={this.handleChannelGroupAcceptClick}
                      />
                    </Grid>
                  )}
                  {this.state.ChannelTargeting === BrandSafetyTrackerBidderTargetingSelected && (
                    <Grid item style={{ width: '400px', marginLeft: 38, marginBottom: 10 }}>
                      <MaterialSearchDropdownMulti
                        getValueFromSearchElement={(src: IListItem) => src.title}
                        selectAll={true}
                        menuTitle='Bidders'
                        list={this.state.bidderList}
                        onChange={(list: IMenuList) => {
                          this.setState({
                            bidderList: list,
                          });
                        }}
                        handleMenuAcceptClick={this.handleBidderAcceptClick}
                        showPrefixID
                      />
                    </Grid>
                  )}
                  {this.state.ChannelTargeting === BrandSafetyTrackerBidderTargetingSelected && (
                    <Grid item style={{ width: '400px', marginLeft: 38, marginBottom: 10 }}>
                      <MaterialSearchDropdownMulti
                        getValueFromSearchElement={(src: IListItem) => src.title}
                        selectAll={true}
                        menuTitle='Bidder Groups'
                        list={this.state.bidderGroupList}
                        onChange={(list: IMenuList) => {
                          this.setState({
                            bidderGroupList: list,
                          });
                        }}
                        handleMenuAcceptClick={this.handleBidderGroupAcceptClick}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {/* Activation - Start/End Dates and AlwaysOn, Scale */}
              <Grid item>
                <Grid
                  container
                  direction='column'
                  justify='flex-start'
                  alignItems='flex-start'
                  spacing={1}
                >
                  {/* Activation */}
                  <Grid item>
                    <Typography className={classes.titleTypography}>Activation</Typography>
                  </Grid>
                  {/* SCALE / Measured Inventory */}
                  <Grid item style={{ marginLeft: 10 }}>
                    <Grid
                      container
                      direction='row'
                      justify='flex-start'
                      alignItems='center'
                      spacing={3}
                    >
                      <Grid item>
                        <Tooltip title='Set the % time when tracker is fired for targeted inventory'>
                          <Typography className={classes.titleTypography}>Scale</Typography>
                        </Tooltip>
                      </Grid>
                      <Grid item style={{ width: 60, marginLeft: 5 }}>
                        <TextField
                          label=''
                          name='MeasuredInventory'
                          value={this.state.MeasuredInventory}
                          onChange={this.handleChange}
                        />
                      </Grid>
                      <Grid item>
                        <Typography style={{ marginLeft: -20 }}>%</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Period */}
                  <Grid item style={{ marginLeft: 10 }}>
                    <Grid
                      container
                      direction='row'
                      justify='flex-start'
                      alignItems='flex-start'
                      spacing={2}
                    >
                      <Grid item>
                        <Typography className={classes.titleTypography}>Period</Typography>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          direction='row'
                          justify='flex-start'
                          alignItems='flex-start'
                          spacing={0}
                        >
                          <Grid
                            item
                            container
                            direction='row'
                            justify='flex-start'
                            alignItems='center'
                            spacing={1}
                          >
                            <Radio
                              onChange={() =>
                                this.setState({ AlwaysOn: BrandSafetyTrackerAlwaysOnActive })
                              }
                              checked={alwaysOn}
                            />
                            <Typography
                              style={{ fontSize: 12, color: alwaysOn ? 'black' : 'grey' }}
                            >
                              Always On
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            container
                            direction='row'
                            justify='flex-start'
                            alignItems='center'
                            spacing={1}
                          >
                            <Radio
                              onChange={() =>
                                this.setState({ AlwaysOn: BrandSafetyTrackerAlwaysOnInactive })
                              }
                              checked={!alwaysOn}
                            />
                            <Typography
                              style={{ fontSize: 12, color: alwaysOn ? 'grey' : 'black' }}
                            >
                              Date Range
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  {!alwaysOn && (
                    <Grid item style={{ marginLeft: 75 }}>
                      <DateTimeRangeComponent
                        timezone={this.state.Timezone}
                        start={this.state.startDateMoment}
                        end={this.state.endDateMoment}
                        onChange={this.handleDateChange}
                        openDirection={'up'}
                        enableSelectAll={true}
                        enablePresets={true}
                      />
                    </Grid>
                  )}
                  {!alwaysOn && (
                    <Grid item style={{ width: 400, marginLeft: 75 }}>
                      <SearchDropdownSingle
                        menuTitle={'Timezone'}
                        list={this.state.timezonesList}
                        onChange={this.setStateCallback}
                        stateKey={'timezonesList'}
                        menuAcceptClickCallbacks={[]}
                        handleMenuAcceptClick={this.handleTimezoneAcceptClick}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          <Divider />
        </Grid>
      );
    };

    // ############################## MAIN RENDER ##############################
    render = () => {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const trackerEvents = this.state.BrandSafetyTrackerEvents.map(
        (trackerEvent: IBrandSafetyTrackerEvent) => {
          return {
            Active: trackerEvent.Active,
            ID: trackerEvent.ID,
            EventType: trackerEvent.EventType,
            PixelURL: trackerEvent.PixelURL,
            Name: trackerEvent.ID.toString(),
            FireOncePerPod: trackerEvent.FireOncePerPod,
          } as ITrackerEvent;
        },
      );
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel largeHorizontalPadding verticalPadding>
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel>
            {/* Top Bar */}
            {this.renderTopBar(classes)}
          </Panel>
          <Panel scroll>
            {/* Tracker */}
            {this.renderTrackerDetails()}

            {/* Events */}
            <Grid item className={classes.mainGridItem}>
              <TrackerEvents
                title={'Tracker Events'}
                trackerEvents={trackerEvents}
                trackerEventTypes={BrandSafetyEventType}
                handleTrackerEventActiveSwitchChange={this.handleTrackerEventActiveSwitchChange}
                handleTrackerEventTypeChange={this.handleTrackerEventTypeChange}
                handleTrackerFireOncePerPodChange={this.handleTrackerFireOncePerPodChange}
                handleTrackerEventPixelURLChange={this.handleTrackerEventPixelURLChange}
                handleDeleteModelWithPromise={this.handleDeleteModelWithPromise}
                handleDeleteSuccess={this.handleDeleteSuccess}
                handleDeleteError={this.handleDeleteError}
                updateTrackerURLsWithErrors={this.updateTrackerURLsWithErrors}
                displaySupportedWikiLink={true}
                publisherTimezone={this.props.session.publisher.Timezone}
              />
            </Grid>
            <Grid item className={classes.mainGridItem}>
              <Typography
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#5865E8',
                  marginBottom: 20,
                  cursor: 'pointer',
                }}
                onClick={this.addEvent}
              >
                + Add Event
              </Typography>
            </Grid>
            {/* SNACKBAR (for notifications)*/}
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    };
  },
);
